import { type MouseEvent, useContext, useEffect } from "react";
import type { HeaderContext, RowData } from "@tanstack/react-table";
import { shallow, useSelector } from "@tanstack/react-store";
import classNames from "classnames";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { DataGridSelectVisibility } from "../columns/types";
import { useDataGridContext } from "../DataGridContext";
import type { DataGridFeatures } from "../features";
import {
    type DataGridSelectAllScope,
    getRowSelectionDisplay,
    getSelectAllState,
    toggleAllInScope,
    toggleRowSelection,
} from "../features/selection/rowSelection";
import { useDisabledReason } from "../features/selection/disabledReason";
import { getLiveRow } from "../grid/useRowState";
import type { DataGridReactTable } from "../types";
import { CellTooltipHostContext } from "./CellTooltipHost";
import { NativeCheckbox } from "./NativeCheckbox";
import type { DataGridCellProps } from "./types";
import styles from "./SelectCell.module.scss";

/** Selection changes in `onClick`, which carries Shift for ranges. */
const ignoreChange = () => undefined;

/** Shift+click extends a range; keep the browser from selecting text. */
const preventShiftSelection = (event: MouseEvent) => {
    if (event.shiftKey) {
        event.preventDefault();
    }
};

interface SelectCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    /** Default `"engaged"`; see `DataGridSelectVisibility`. */
    visibility?: DataGridSelectVisibility;
}

/**
 * Row checkbox: a native input, since one renders in every row. It stays out
 * of the tab order: the grid is one tab stop and Space toggles selection on
 * the focused row. Shift+click selects the range from the last toggled row.
 * A disabled row shows its reason on hover, through the table's shared
 * tooltip when there is one.
 */
export const SelectCell = <TData extends RowData>({
    context,
    visibility = "engaged",
}: Readonly<SelectCellProps<TData>>) => {
    const row = context.row;
    const display = useSelector(row.table.atoms.rowSelection, () =>
        getRowSelectionDisplay(getLiveRow(row)),
    );
    const canSelect = row.getCanSelect();
    const disabledReason = useDisabledReason(row);
    const hasTooltipHost = useContext(CellTooltipHostContext);
    const hasOwnTooltip = !hasTooltipHost && !canSelect && !!disabledReason;
    const selectClassName = classNames(styles.select, {
        [styles.engaged]: visibility === "engaged",
    });
    const checkbox = (
        <NativeCheckbox
            className={hasOwnTooltip ? undefined : selectClassName}
            checked={display === "all"}
            indeterminate={display === "some"}
            disabled={!canSelect}
            tooltipText={hasTooltipHost ? disabledReason : undefined}
            onMouseDown={preventShiftSelection}
            onClick={(event) =>
                toggleRowSelection({
                    row: getLiveRow(row),
                    isRangeEvent: event.shiftKey,
                })
            }
            onChange={ignoreChange}
            aria-label="Select row"
            tabIndex={-1}
            data-dt-interactive=""
        />
    );
    return hasOwnTooltip ? (
        <Tooltip content={disabledReason}>
            <span
                className={classNames(selectClassName, styles.disabledSelect)}
            >
                {checkbox}
            </span>
        </Tooltip>
    ) : (
        checkbox
    );
};

/**
 * Marks the table root with `data-dt-has-selection` while any row is
 * selected, so engaged checkboxes show on every row without re-rendering
 * them.
 */
const useHasSelectionMarker = <TData extends RowData>(
    context: HeaderContext<DataGridFeatures, TData>,
) => {
    const { rootElement } = useDataGridContext();
    const hasSelection = useSelector(
        context.table.atoms.rowSelection,
        (selection) => Object.values(selection).some(Boolean),
    );
    useEffect(() => {
        if (!rootElement || !hasSelection) {
            return undefined;
        }
        rootElement.setAttribute("data-dt-has-selection", "");
        return () => {
            rootElement.removeAttribute("data-dt-has-selection");
        };
    }, [rootElement, hasSelection]);
};

interface SelectAllHeaderProps<TData extends RowData> {
    context: HeaderContext<DataGridFeatures, TData>;
}

interface SelectAllCheckboxProps<TData extends RowData> {
    table: DataGridReactTable<TData>;
    scope: DataGridSelectAllScope;
}

/**
 * The header checkbox over the grid's current table, which changes as rows
 * load and pages turn, so the selection selector re-runs then too.
 */
const SelectAllCheckbox = <TData extends RowData>({
    table,
    scope,
}: Readonly<SelectAllCheckboxProps<TData>>) => {
    const selection = useSelector(
        table.atoms.rowSelection,
        () => getSelectAllState(table, scope),
        { compare: shallow },
    );
    return (
        <Checkbox
            checked={selection.isAll}
            indeterminate={!selection.isAll && selection.isSome}
            onCheckedChange={(isChecked) =>
                toggleAllInScope({ table, scope, isSelected: isChecked })
            }
            aria-label="Select all rows"
            tabIndex={-1}
            data-dt-interactive=""
        />
    );
};

/**
 * Header checkbox, always shown whatever the column's `visibility`; the only
 * header part that subscribes to row selection.
 */
export const SelectAllHeader = <TData extends RowData>({
    context,
}: Readonly<SelectAllHeaderProps<TData>>) => {
    const dataGrid = useDataGridContext();
    useHasSelectionMarker(context);
    return dataGrid.visit((instance) => (
        <SelectAllCheckbox
            table={instance.table}
            scope={
                instance.optionsRef.current.rowSelection?.selectAllScope ??
                "all"
            }
        />
    ));
};
